import type { MiceOrderView, MiceTraveler } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Dialog, DialogContent, DialogTitle } from '@web/ui/core';
import { formatLegDay } from './need_format';

interface VerifyPurchaseDialogProps {
  traveler: MiceTraveler | null;
  order: MiceOrderView | null;
  /** "Não há reserva": o pedido volta para a fila. */
  onRelease: (orderId: string) => void;
  /** "Há reserva": nada a chamar; o acompanhamento vincula o voucher. */
  onWait: () => void;
  onClose: () => void;
}

/**
 * Indeterminado guiado: a reserva pode ter sido feita, então o operador confere na plataforma
 * antes de liberar uma nova tentativa (que, com reserva feita, compraria duas vezes).
 */
export function VerifyPurchaseDialog(props: Readonly<VerifyPurchaseDialogProps>) {
  const { traveler, order } = props;
  const date = order?.outbound?.date;
  return (
    <Dialog
      open={traveler !== null && order !== null}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <DialogContent>
        <DialogTitle>{t('mice.verify.title')}</DialogTitle>
        {traveler && order ? (
          <div className="flex flex-col gap-3 text-meta text-foreground-default">
            {order.error ? <p className="text-destructive">{order.error.message}</p> : null}
            <p>{t('mice.release.body')}</p>
            <p>
              <span className="mr-2 font-mono text-primary">1</span>
              {t('mice.verify.step1', {
                name: traveler.name ?? traveler.email,
                email: traveler.email,
                date: date ? formatLegDay(date) : '—',
              })}
            </p>
            <p>
              <span className="mr-2 font-mono text-primary">2</span>
              {t('mice.verify.step2')}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Button
                  variant="outline"
                  onClick={() => {
                    props.onRelease(order.id);
                  }}
                >
                  {t('mice.verify.none')}
                </Button>
                <span className="text-micro text-muted-foreground">
                  {t('mice.verify.noneHint')}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <Button variant="outline" onClick={props.onWait}>
                  {t('mice.verify.found')}
                </Button>
                <span className="text-micro text-muted-foreground">
                  {t('mice.verify.foundHint')}
                </span>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="ghost" onClick={props.onClose}>
                {t('mice.verify.cancel')}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
